import { infiniteQueryOptions, queryOptions, type InfiniteData } from "@tanstack/react-query";
import { authFetch } from "@/lib/api-client";
import { abortableSleep, fetchAmberOptions } from "@/lib/queries/amber-options";
import { fetchCreativeAdSpendSection } from "@/features/explore/lib/creative-ad-spend";
import {
  resolveDateWindow,
  windowSpanDays,
  type AdvancedFilters,
  type CreativeGrouping,
  type DrawerWhySection,
} from "@nexus/contracts";
import {
  POLL_INTERVAL_MS,
  MAX_POLL_DURATION_MS,
  type CompetitorFiltersInput,
  type MyBrandFiltersInput,
} from "@/lib/queries/dashboard.queries";
import { GC_TIME_DEFAULT } from "@/lib/query-constants";
import { buildDrilldownQs } from "@/lib/queries/drilldown-query-string";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Page-scoped drilldown filters (mirrors `DrilldownPageFiltersSchema`, minus `offset`). */
export interface DrilldownPageFiltersInput {
  objectType: string[];
  effectiveStatus: string[];
  audienceSegment: string[];
  device: string[];
  aspectRatio: string[];
  startDate: string;
  endDate: string;
  segment1: string;
  segment2: string;
  segment3: string;
  sortMode: string;
  sortDirection: "asc" | "desc";
  advancedFilters: AdvancedFilters;
}

/** Canonical Retool fatigue BQ query parameter names. */
export const FATIGUE_CANONICAL_QUERY_PARAM_KEYS = [
  "search",
  "effective_status",
  "account",
  "exclude_search",
  "hash",
  "product",
  "days_back",
  "coefficient",
  "userName",
  "group_by",
  "ad_id",
  "find",
  "object_type",
  "audience_segment",
  "device",
  "aspectRatio",
  "source",
  "conversions",
  "exclude_find",
  "weekly_conversions",
] as const;

/**
 * Inclusive day count of the window the BFF will query — a single-day window is 1.
 *
 * Mirrors the BFF's `resolveDateWindow`: a real, ordered pair wins, and anything
 * else (absent, malformed, impossible, reversed) falls back to the token's
 * `[today - (N - 1), today]`, which spans N days. Callers need the span before
 * the request to size buckets and to decide which granularities the window can support.
 */
export function drilldownWindowSpanDays(
  drilldownFilters: Pick<DrilldownPageFiltersInput, "startDate" | "endDate">,
  dateRange: string,
): number {
  return windowSpanDays(
    resolveDateWindow({
      dateRange,
      startDate: drilldownFilters.startDate,
      endDate: drilldownFilters.endDate,
    }),
  );
}

function toFatigueFilterString(values: string[]): string {
  const filtered = values
    .map((v) => v.trim())
    .filter((v) => v !== "" && v.toLowerCase() !== "all");
  return filtered.length > 0 ? filtered.join(",") : "All";
}

function toFatigueAudienceFilterString(values: string[]): string {
  const filtered = values.map((v) => v.trim()).filter((v) => v !== "");
  return filtered.length > 0 ? filtered.join(",") : "All";
}

export function pickFatigueCanonicalQueryParams(
  inputs: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!inputs) return {};
  const picked: Record<string, unknown> = {};
  for (const key of FATIGUE_CANONICAL_QUERY_PARAM_KEYS) {
    if (key in inputs) picked[key] = inputs[key];
  }
  return picked;
}

/** Client-side preview of BQ params before the workspace handler resolves tenant knobs. */
export function myBrandDrawerFatigueQueryParamsPreview(
  hash: string,
  adId: string | null,
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
): Record<string, string | number> {
  return {
    search: "All",
    effective_status: toFatigueFilterString(drilldownFilters.effectiveStatus),
    account: toFatigueFilterString(filters.accounts),
    exclude_search: "All",
    hash,
    product: toFatigueFilterString(filters.products),
    // `days_back` is Retool's parameter name and stays the debug panel's contract; the query
    // itself is bound on the window, so the value it previews is that window's span.
    days_back: drilldownWindowSpanDays(drilldownFilters, filters.dateRange),
    coefficient: 1,
    userName: "(resolved server-side)",
    group_by: "All",
    ad_id: adId ?? "",
    find: "All",
    object_type: toFatigueFilterString(drilldownFilters.objectType),
    audience_segment: toFatigueAudienceFilterString(drilldownFilters.audienceSegment),
    device: toFatigueAudienceFilterString(drilldownFilters.device),
    aspectRatio: toFatigueAudienceFilterString(drilldownFilters.aspectRatio),
    source: toFatigueFilterString(filters.platforms),
    conversions: "(resolved server-side)",
    exclude_find: "All",
    weekly_conversions: "(resolved server-side)",
  };
}

/** Number of rows per page — must match the backend `DRILLDOWN_DEFAULTS.limitBy` /
 *  `DRILLDOWN_MY_BRAND_DEFAULTS.limitBy`. Sized to exceed a typical viewport's worth
 *  of grid rows at the current card density (6-col × 8 rows) — a smaller page would
 *  make useInfiniteScroll's fill-the-screen behavior fetch a second page immediately
 *  on every load. */
export const DRILLDOWN_PAGE_SIZE = 48;

/**
 * List footer totals. Competitors fills Days / Assets / DaysPerAsset.
 * My Brand fills `spend` (the full set, before the page limit) and `placeholderSpend`,
 * the part of `spend` with no row (placeholder creative IDs, grouped by Creative).
 */
export interface DrilldownListTotals {
  Days?: number;
  Assets?: number;
  DaysPerAsset?: number;
  spend?: number;
  placeholderSpend?: number;
}

/** The single combined query section — resolved inputs + raw rows.
 *  The compiled BigQuery text is stripped by the API (`stripWorkspaceSql`) and is
 *  deliberately absent here: it would expose every tenant member's KPI formulas. */
export interface DrilldownQuerySection {
  inputs: Record<string, unknown>;
  rows: Array<Record<string, unknown>>;
  totals?: DrilldownListTotals | null;
}

export interface DrilldownData {
  mode: "competitors" | "my-brand";
  section: DrilldownQuerySection | null;
}

export type DrilldownStatusData = {
  status: "ACCEPTED" | "PROCESSING" | "COMPLETED" | "FAILED";
  mode?: "competitors" | "my-brand";
  section?: DrilldownQuerySection | null;
};

/** CreativeHashData payload backing the competitor drawer's Info tab narrative. */
export interface CreativeHashDataResponse {
  inputs: Record<string, unknown>;
  rows: Array<Record<string, unknown>>;
  sql: string;
}

/** Pages that open the competitor drawer; each reads CreativeHashData under its own prefix. */
export type CreativeHashDataScope = "drilldown" | "dashboard";

/** One materialized page of the infinite query. */
export interface DrilldownPage {
  rows: Array<Record<string, unknown>>;
  inputs?: Record<string, unknown>;
  offset: number;
  totals?: DrilldownListTotals | null;
}

// ─── Keys ─────────────────────────────────────────────────────────────────────

export const drilldownKeys = {
  all: ["drilldown"] as const,
  data: () => [...drilldownKeys.all, "data"] as const,
  // Offset is the page param — NOT part of the key (all pages share one infinite query).
  dataFor: (
    tenantId: string,
    filters: CompetitorFiltersInput,
    drilldownFilters: DrilldownPageFiltersInput,
  ) => [...drilldownKeys.data(), "competitors", tenantId, filters, drilldownFilters] as const,
  myBrandDataFor: (
    tenantId: string,
    filters: MyBrandFiltersInput,
    drilldownFilters: DrilldownPageFiltersInput,
  ) => [...drilldownKeys.data(), "my-brand", tenantId, filters, drilldownFilters] as const,
  creativeHashData: (
    scope: CreativeHashDataScope,
    hash: string,
    industry: string,
    startDate: string,
    endDate: string,
    dateRange: string,
    libId: string | null,
  ) =>
    [...drilldownKeys.all, "creative-hash-data", scope, hash, industry, startDate, endDate, dateRange, libId] as const,
  myBrandDrawerData: (
    hash: string,
    adId: string | null,
    filters: MyBrandFiltersInput,
    drilldownFilters: DrilldownPageFiltersInput,
  ) => [...drilldownKeys.all, "my-brand-drawer", hash, adId, filters, drilldownFilters] as const,
  myBrandDrawerPerformanceData: (
    hash: string,
    adId: string | null,
    filters: MyBrandFiltersInput,
    drilldownFilters: DrilldownPageFiltersInput,
  ) =>
    [...drilldownKeys.all, "my-brand-drawer-performance", hash, adId, filters, drilldownFilters] as const,
  myBrandDrawerWhyData: (
    hash: string,
    filters: MyBrandFiltersInput,
    drilldownFilters: DrilldownPageFiltersInput,
  ) =>
    // Only the fields that change the data: a sort change must not reload the tab.
    [
      ...drilldownKeys.all,
      "my-brand-drawer-why",
      hash,
      filters,
      {
        objectType: drilldownFilters.objectType,
        effectiveStatus: drilldownFilters.effectiveStatus,
        audienceSegment: drilldownFilters.audienceSegment,
        device: drilldownFilters.device,
        aspectRatio: drilldownFilters.aspectRatio,
        startDate: drilldownFilters.startDate,
        endDate: drilldownFilters.endDate,
        segment1: drilldownFilters.segment1,
        segment2: drilldownFilters.segment2,
        segment3: drilldownFilters.segment3,
        advancedFilters: drilldownFilters.advancedFilters,
      },
    ] as const,
  myBrandCreativeAds: (
    tenantId: string,
    hash: string,
    grouping: CreativeGrouping,
    filters: MyBrandFiltersInput,
    drilldownFilters: DrilldownPageFiltersInput,
  ) =>
    [...drilldownKeys.all, "my-brand-creative-ads", tenantId, hash, grouping, filters, drilldownFilters] as const,
  myBrandDrawerFatigueData: (
    hash: string,
    adId: string | null,
    filters: MyBrandFiltersInput,
    drilldownFilters: DrilldownPageFiltersInput,
    granularity: string,
    metrics: string[],
  ) =>
    [
      ...drilldownKeys.all,
      "my-brand-drawer-fatigue",
      hash,
      adId,
      granularity,
      metrics,
      filters,
      drilldownFilters,
    ] as const,
  groupByOptions: (mode: "competitors" | "my-brand") =>
    [...drilldownKeys.all, "group-by-options", mode] as const,
};

// ─── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Amber Path poll loop: hit `/drilldown/status/:id` until the job reaches a
 * terminal state, then return the combined data. Collapses the async round-trip
 * into a single awaitable so it fits an infinite-query `queryFn` cleanly (the
 * same shape `useAmberMutation` uses via `pollStatus`).
 */
async function pollDrilldownStatus(
  correlationId: string,
  signal?: AbortSignal,
): Promise<DrilldownData> {
  const startedAt = Date.now();
  while (Date.now() - startedAt < MAX_POLL_DURATION_MS) {
    const { data } = await authFetch<DrilldownStatusData>(
      `/drilldown/status/${correlationId}`,
      { signal },
    );
    if (data?.status === "COMPLETED") {
      return { mode: data.mode ?? "competitors", section: data.section ?? null };
    }
    if (data?.status === "FAILED") {
      throw new Error("Drilldown request failed.");
    }
    await abortableSleep(POLL_INTERVAL_MS, signal);
  }
  throw new Error("Drilldown request timed out.");
}

// ─── Query options ────────────────────────────────────────────────────────────

export function drilldownDataInfiniteQueryOptions(
  tenantId: string | null,
  filters: CompetitorFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
) {
  return infiniteQueryOptions<DrilldownPage, Error, InfiniteData<DrilldownPage, number>, ReturnType<typeof drilldownKeys.dataFor>, number>({
    queryKey: drilldownKeys.dataFor(tenantId ?? "", filters, drilldownFilters),
    queryFn: async ({ pageParam, signal }) => {
      const { data, correlationId } = await authFetch<DrilldownData>(
        `/drilldown/data?${buildDrilldownQs(filters, drilldownFilters, pageParam)}`,
        { signal },
      );
      // Cached → data returned synchronously; otherwise 202 + correlationId → poll.
      const resolved = data ?? (await pollDrilldownStatus(correlationId!, signal));
      return {
        rows: resolved.section?.rows ?? [],
        inputs: resolved.section?.inputs,
        offset: pageParam,
        totals: resolved.section?.totals ?? null,
      };
    },
    initialPageParam: 0,
    // A short final page (fewer than a full page of rows) means no more data.
    getNextPageParam: (lastPage) =>
      lastPage.rows.length === DRILLDOWN_PAGE_SIZE ? lastPage.offset + DRILLDOWN_PAGE_SIZE : undefined,
    enabled: !!tenantId,
    // Async-refresh pattern: refetch only when the filter queryKey changes —
    // a background refetch would issue a fresh 202 and wipe the rendered pages.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

// ─── My Brand ─────────────────────────────────────────────────────────────────

function buildMyBrandDrilldownQs(
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
  offset: number,
): string {
  const qs = new URLSearchParams({ mode: "my-brand", dateRange: filters.dateRange });
  // Always send platforms (even empty): the drilldown API defaults a *missing* param to
  // ['meta'], so an empty string is how "all platforms" reaches the backend. Omitting it
  // made My Brand Drilldown silently Meta-only (AD-3421).
  qs.set("platforms", filters.platforms.join(","));
  // Accounts and products — omit when empty so the backend applies the "All" bypass.
  if (filters.accounts.length > 0) qs.set("accounts", filters.accounts.join(","));
  if (filters.products.length > 0) qs.set("products", filters.products.join(","));
  // Page filters. Unlike the competitors path (whose SQL treats the string "All" as a
  // bypass sentinel), the My Brand builder uses array params with an empty-array bypass —
  // so OMIT these when empty. Sending "All" would parse to `['All']` and filter for the
  // literal value 'All', returning zero rows.
  if (drilldownFilters.objectType.length > 0) qs.set("objectType", drilldownFilters.objectType.join(","));
  if (drilldownFilters.effectiveStatus.length > 0) qs.set("effectiveStatus", drilldownFilters.effectiveStatus.join(","));
  if (drilldownFilters.audienceSegment.length > 0) qs.set("audienceSegment", drilldownFilters.audienceSegment.join(","));
  if (drilldownFilters.device.length > 0) qs.set("device", drilldownFilters.device.join(","));
  if (drilldownFilters.aspectRatio.length > 0) qs.set("aspectRatio", drilldownFilters.aspectRatio.join(","));
  if (drilldownFilters.startDate) qs.set("startDate", drilldownFilters.startDate);
  if (drilldownFilters.endDate) qs.set("endDate", drilldownFilters.endDate);
  qs.set("segment1", drilldownFilters.segment1);
  qs.set("segment2", drilldownFilters.segment2);
  qs.set("segment3", drilldownFilters.segment3);
  qs.set("sortMode", drilldownFilters.sortMode);
  qs.set("sortDirection", drilldownFilters.sortDirection);
  if (drilldownFilters.advancedFilters.length > 0) {
    qs.set("advancedFilters", JSON.stringify(drilldownFilters.advancedFilters));
  }
  qs.set("offset", String(offset));
  return qs.toString();
}

export function drilldownMyBrandDataInfiniteQueryOptions(
  tenantId: string | null,
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
) {
  return infiniteQueryOptions<DrilldownPage, Error, InfiniteData<DrilldownPage, number>, ReturnType<typeof drilldownKeys.myBrandDataFor>, number>({
    queryKey: drilldownKeys.myBrandDataFor(tenantId ?? "", filters, drilldownFilters),
    queryFn: async ({ pageParam, signal }) => {
      const { data, correlationId } = await authFetch<DrilldownData>(
        `/drilldown/data?${buildMyBrandDrilldownQs(filters, drilldownFilters, pageParam)}`,
        { signal },
      );
      const resolved = data ?? (await pollDrilldownStatus(correlationId!, signal));
      return {
        rows: resolved.section?.rows ?? [],
        inputs: resolved.section?.inputs,
        offset: pageParam,
        totals: resolved.section?.totals ?? null,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.rows.length === DRILLDOWN_PAGE_SIZE ? lastPage.offset + DRILLDOWN_PAGE_SIZE : undefined,
    enabled: !!tenantId,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Creative-hash breakdown for the competitor drawer. Backed by the workspace worker: a
 * warm cache answers `200 { data }`, a cold miss answers `202 { correlationId }`.
 *
 * `rereadOnComplete` because this result can be large — the worker writes the rows to the
 * result cache only and leaves them off the correlation status, so a COMPLETED status is
 * a signal to re-read this endpoint rather than a carrier for the payload.
 */
export function creativeHashDataQueryOptions(
  scope: CreativeHashDataScope,
  hash: string,
  industry: string,
  startDate: string,
  endDate: string,
  dateRange: string,
  libId: string | null,
  enabled: boolean,
) {
  const qs = new URLSearchParams({ hash, industry, startDate, endDate, dateRange });
  if (libId) qs.set("libId", libId);
  return {
    queryKey: drilldownKeys.creativeHashData(scope, hash, industry, startDate, endDate, dateRange, libId),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchAmberOptions<CreativeHashDataResponse>(
        `/${scope}/creative-hash-data?${qs.toString()}`,
        `/${scope}`,
        { signal, rereadOnComplete: true },
      ),
    enabled: enabled && hash.length > 0 && industry.length > 0,
    staleTime: Infinity,
    gcTime: GC_TIME_DEFAULT,
    retry: false,
  };
}

function buildMyBrandDrawerQs(
  hash: string,
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
  adId?: string | null,
): string {
  const qs = new URLSearchParams({ hash, dateRange: filters.dateRange });
  if (adId) qs.set("adId", adId);
  // Always send platforms (even empty) — see buildMyBrandDrilldownQs.
  qs.set("platforms", filters.platforms.join(","));
  if (filters.accounts.length > 0) qs.set("accounts", filters.accounts.join(","));
  if (filters.products.length > 0) qs.set("products", filters.products.join(","));
  if (drilldownFilters.objectType.length > 0) qs.set("objectType", drilldownFilters.objectType.join(","));
  if (drilldownFilters.effectiveStatus.length > 0) qs.set("effectiveStatus", drilldownFilters.effectiveStatus.join(","));
  if (drilldownFilters.audienceSegment.length > 0) qs.set("audienceSegment", drilldownFilters.audienceSegment.join(","));
  if (drilldownFilters.device.length > 0) qs.set("device", drilldownFilters.device.join(","));
  if (drilldownFilters.aspectRatio.length > 0) qs.set("aspectRatio", drilldownFilters.aspectRatio.join(","));
  if (drilldownFilters.startDate) qs.set("startDate", drilldownFilters.startDate);
  if (drilldownFilters.endDate) qs.set("endDate", drilldownFilters.endDate);
  qs.set("segment1", drilldownFilters.segment1);
  qs.set("segment2", drilldownFilters.segment2);
  qs.set("segment3", drilldownFilters.segment3);
  qs.set("sortMode", drilldownFilters.sortMode);
  qs.set("sortDirection", drilldownFilters.sortDirection);
  if (drilldownFilters.advancedFilters.length > 0) {
    qs.set("advancedFilters", JSON.stringify(drilldownFilters.advancedFilters));
  }
  return qs.toString();
}

function buildMyBrandDrawerPerformanceQs(
  hash: string,
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
  adId?: string | null,
): string {
  const qs = new URLSearchParams({ hash, dateRange: filters.dateRange });
  if (adId) qs.set("adId", adId);
  // Always send platforms (even empty) — see buildMyBrandDrilldownQs.
  qs.set("platforms", filters.platforms.join(","));
  if (filters.accounts.length > 0) qs.set("accounts", filters.accounts.join(","));
  if (filters.products.length > 0) qs.set("products", filters.products.join(","));
  if (drilldownFilters.objectType.length > 0) qs.set("objectType", drilldownFilters.objectType.join(","));
  if (drilldownFilters.effectiveStatus.length > 0) {
    qs.set("effectiveStatus", drilldownFilters.effectiveStatus.join(","));
  }
  if (drilldownFilters.audienceSegment.length > 0) qs.set("audienceSegment", drilldownFilters.audienceSegment.join(","));
  if (drilldownFilters.device.length > 0) qs.set("device", drilldownFilters.device.join(","));
  if (drilldownFilters.aspectRatio.length > 0) qs.set("aspectRatio", drilldownFilters.aspectRatio.join(","));
  if (drilldownFilters.startDate) qs.set("startDate", drilldownFilters.startDate);
  if (drilldownFilters.endDate) qs.set("endDate", drilldownFilters.endDate);
  qs.set("segment1", drilldownFilters.segment1);
  qs.set("segment2", drilldownFilters.segment2);
  qs.set("segment3", drilldownFilters.segment3);
  qs.set("sortMode", drilldownFilters.sortMode);
  qs.set("sortDirection", drilldownFilters.sortDirection);
  if (drilldownFilters.advancedFilters.length > 0) {
    qs.set("advancedFilters", JSON.stringify(drilldownFilters.advancedFilters));
  }
  return qs.toString();
}

function buildMyBrandDrawerFatigueQs(
  hash: string,
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
  metrics: string[],
  granularity: string,
  adId?: string | null,
): string {
  const qs = new URLSearchParams({
    hash,
    dateRange: filters.dateRange,
    metrics: metrics.join(","),
    granularity,
  });
  if (adId) qs.set("adId", adId);
  // Always send platforms (even empty) — see buildMyBrandDrilldownQs.
  qs.set("platforms", filters.platforms.join(","));
  if (filters.accounts.length > 0) qs.set("accounts", filters.accounts.join(","));
  if (filters.products.length > 0) qs.set("products", filters.products.join(","));
  if (drilldownFilters.objectType.length > 0) qs.set("objectType", drilldownFilters.objectType.join(","));
  if (drilldownFilters.effectiveStatus.length > 0) {
    qs.set("effectiveStatus", drilldownFilters.effectiveStatus.join(","));
  }
  if (drilldownFilters.audienceSegment.length > 0) qs.set("audienceSegment", drilldownFilters.audienceSegment.join(","));
  if (drilldownFilters.device.length > 0) qs.set("device", drilldownFilters.device.join(","));
  if (drilldownFilters.aspectRatio.length > 0) qs.set("aspectRatio", drilldownFilters.aspectRatio.join(","));
  if (drilldownFilters.startDate) qs.set("startDate", drilldownFilters.startDate);
  if (drilldownFilters.endDate) qs.set("endDate", drilldownFilters.endDate);
  qs.set("segment1", drilldownFilters.segment1);
  qs.set("segment2", drilldownFilters.segment2);
  qs.set("segment3", drilldownFilters.segment3);
  qs.set("sortMode", drilldownFilters.sortMode);
  qs.set("sortDirection", drilldownFilters.sortDirection);
  if (drilldownFilters.advancedFilters.length > 0) {
    qs.set("advancedFilters", JSON.stringify(drilldownFilters.advancedFilters));
  }
  return qs.toString();
}

export interface MyBrandDrawerFatigueHttpTransportSnapshot {
  method: "GET";
  endpoint: string;
  queryString: string;
}

export function myBrandDrawerFatigueHttpTransportSnapshot(
  hash: string,
  adId: string | null,
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
  metrics: string[],
  granularity: string,
): MyBrandDrawerFatigueHttpTransportSnapshot {
  const sortedMetrics = [...metrics].sort();
  const queryString = buildMyBrandDrawerFatigueQs(
    hash,
    filters,
    drilldownFilters,
    sortedMetrics,
    granularity,
    adId,
  );
  return {
    method: "GET",
    endpoint: "/drilldown/my-brand-creative-fatigue",
    queryString,
  };
}

/** @deprecated Use myBrandDrawerFatigueHttpTransportSnapshot — HTTP layer only. */
export const myBrandDrawerFatigueRequestSnapshot = myBrandDrawerFatigueHttpTransportSnapshot;

export function myBrandDrawerDataQueryOptions(
  hash: string,
  adId: string | null,
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
  enabled: boolean,
) {
  return {
    queryKey: drilldownKeys.myBrandDrawerData(hash, adId, filters, drilldownFilters),
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const { data, correlationId } = await authFetch<DrilldownData>(
        `/drilldown/my-brand-creative-data?${buildMyBrandDrawerQs(hash, filters, drilldownFilters, adId)}`,
        { signal },
      );
      const resolved = data ?? (await pollDrilldownStatus(correlationId!, signal));
      if (!resolved.section) {
        throw new Error("My Brand drawer data request returned no section.");
      }
      return resolved.section;
    },
    enabled: enabled && hash.length > 0,
    staleTime: Infinity,
    gcTime: GC_TIME_DEFAULT,
  };
}

export function myBrandDrawerPerformanceDataQueryOptions(
  hash: string,
  adId: string | null,
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
  enabled: boolean,
) {
  return {
    queryKey: drilldownKeys.myBrandDrawerPerformanceData(
      hash,
      adId,
      filters,
      drilldownFilters,
    ),
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const { data, correlationId } = await authFetch<DrilldownData>(
        `/drilldown/my-brand-creative-performance?${buildMyBrandDrawerPerformanceQs(hash, filters, drilldownFilters, adId)}`,
        { signal },
      );
      const resolved = data ?? (await pollDrilldownStatus(correlationId!, signal));
      if (!resolved.section) {
        throw new Error("My Brand drawer performance request returned no section.");
      }
      return resolved.section;
    },
    enabled: enabled && hash.length > 0,
    staleTime: Infinity,
    gcTime: GC_TIME_DEFAULT,
  };
}

/**
 * "Why it works" tab: the creative's tags vs the account's own creatives with each tag, in
 * every KPI of the user's list that has data (the tab switches KPI without a new request).
 */
export function myBrandDrawerWhyDataQueryOptions(
  hash: string,
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
  enabled: boolean,
) {
  return {
    queryKey: drilldownKeys.myBrandDrawerWhyData(hash, filters, drilldownFilters),
    // `null` = the tenant has no tag data to read (no customer partition); the tab shows a
    // neutral empty state for it instead of an error.
    queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<DrawerWhySection | null> => {
      const qs = new URLSearchParams(buildMyBrandDrawerQs(hash, filters, drilldownFilters));
      // Sort does not change this aggregate; the route's cache key leaves it out too.
      qs.delete("sortMode");
      qs.delete("sortDirection");
      const { data, correlationId } = await authFetch<DrilldownData>(
        `/drilldown/my-brand-creative-why?${qs.toString()}`,
        { signal },
      );
      const resolved = data ?? (await pollDrilldownStatus(correlationId!, signal));
      return (resolved.section as unknown as DrawerWhySection | null) ?? null;
    },
    enabled: enabled && hash.length > 0,
    staleTime: Infinity,
    gcTime: GC_TIME_DEFAULT,
  };
}

/** Querystring for `GET /drilldown/my-brand-creative-ads`. */
export function buildMyBrandCreativeAdsQs(
  hash: string,
  grouping: CreativeGrouping,
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
): string {
  const qs = new URLSearchParams(buildMyBrandDrawerQs(hash, filters, drilldownFilters));
  qs.set("creativeGrouping", JSON.stringify(grouping));
  return qs.toString();
}

export function myBrandCreativeAdsQueryOptions(
  tenantId: string | null,
  hash: string,
  grouping: CreativeGrouping,
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
  enabled: boolean,
) {
  return queryOptions({
    queryKey: drilldownKeys.myBrandCreativeAds(tenantId ?? "", hash, grouping, filters, drilldownFilters),
    queryFn: ({ signal }) =>
      fetchCreativeAdSpendSection(
        `/drilldown/my-brand-creative-ads?${buildMyBrandCreativeAdsQs(hash, grouping, filters, drilldownFilters)}`,
        "/drilldown",
        signal,
      ),
    enabled: enabled && !!tenantId && hash.length > 0,
    staleTime: Infinity,
    gcTime: GC_TIME_DEFAULT,
    retry: false,
  });
}

export function myBrandDrawerFatigueDataQueryOptions(
  hash: string,
  adId: string | null,
  filters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
  metrics: string[],
  granularity: string,
  enabled: boolean,
) {
  const sortedMetrics = [...metrics].sort();
  return {
    queryKey: drilldownKeys.myBrandDrawerFatigueData(
      hash,
      adId,
      filters,
      drilldownFilters,
      granularity,
      sortedMetrics,
    ),
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const { data, correlationId } = await authFetch<DrilldownData>(
        `/drilldown/my-brand-creative-fatigue?${buildMyBrandDrawerFatigueQs(
          hash,
          filters,
          drilldownFilters,
          sortedMetrics,
          granularity,
          adId,
        )}`,
        { signal },
      );
      const resolved = data ?? (await pollDrilldownStatus(correlationId!, signal));
      if (!resolved.section) {
        throw new Error("My Brand drawer fatigue request returned no section.");
      }
      return resolved.section;
    },
    enabled: enabled && hash.length > 0 && metrics.length > 0,
    staleTime: Infinity,
    gcTime: GC_TIME_DEFAULT,
  };
}

export interface DrilldownGroupByOption {
  label: string;
  value: string;
}

/**
 * Drilldown Group By dimension list from `features_mapping` (Type='Drilldown'),
 * platform-scoped by Explore mode. `mode` is required — the BFF no longer
 * defaults a missing param to competitors (that mixed Comp Features into My Brand).
 */
export function drilldownGroupByOptionsQueryOptions(mode: "competitors" | "my-brand") {
  const qs = new URLSearchParams({ mode });
  return queryOptions<DrilldownGroupByOption[]>({
    queryKey: drilldownKeys.groupByOptions(mode),
    queryFn: async ({ signal }) => {
      const { data } = await authFetch<DrilldownGroupByOption[]>(
        `/drilldown/group-by-options?${qs.toString()}`,
        { signal },
      );
      return data ?? [];
    },
    staleTime: Infinity,
    retry: false,
    refetchOnReconnect: false,
  });
}
